import { useQuery } from '@tanstack/react-query';
import {
    type CredentialRotationAttemptState,
    type CredentialRotationHistoryResponse,
} from '@anchorbase/pillar-sdk';
import { ipxCredentialManagementApi } from '@/lib/pillarApi';
import { buildRoleScopedQueryKey } from '@/lib/reactQueryRoleScope';
import { useAuthStore } from '@/store/auth';

export interface CredentialRotationHistoryQueryParams {
    readonly credentialUuid: string | undefined;
    readonly limit?: number;
    readonly cursor?: string;
    readonly state?: CredentialRotationAttemptState;
    readonly enabled?: boolean;
}

export function buildCredentialRotationHistoryQueryKey(
    activeRoleAssignmentId: number | null,
    params: CredentialRotationHistoryQueryParams
): readonly unknown[] {
    return buildRoleScopedQueryKey(activeRoleAssignmentId, [
        'credential-rotation-history',
        params.credentialUuid,
        params.limit ?? null,
        params.cursor ?? null,
        params.state ?? null,
    ]);
}

export function useCredentialRotationHistory(
    params: CredentialRotationHistoryQueryParams
) {
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );
    return useQuery<CredentialRotationHistoryResponse>({
        queryKey: buildCredentialRotationHistoryQueryKey(
            activeRoleAssignmentId,
            params
        ),
        queryFn: async () => {
            if (params.credentialUuid === undefined) {
                throw new Error(
                    'Missing credential UUID for rotation history query'
                );
            }
            return await ipxCredentialManagementApi.listCredentialRotationHistory(
                {
                    credential_uuid: params.credentialUuid,
                    limit: params.limit,
                    cursor: params.cursor,
                    state: params.state,
                }
            );
        },
        enabled:
            params.credentialUuid !== undefined &&
            (params.enabled === undefined || params.enabled),
    });
}
